import { ApolloError } from '@apollo/client/errors';

import {
  BOOLEAN_VALUES,
  COLOR_LIST_VALUE_OPERATORS,
  COLOR_VALUELESS_OPERATORS,
  LIST_OPERATORS,
  MATCHING_CROSS_ENTITY_OPERATORS,
  MATCHING_LIST_VALUE_OPERATORS,
  MATCHING_NOTES_SECTION_VALUES,
  MATCHING_NUMERIC_OPERATORS,
  MATCHING_RIGHT_ONLY_OPERATORS,
  MATCHING_VALUELESS_OPERATORS,
  NUMERIC_OPERATORS,
  NUMERIC_VALUE_PATTERN,
  UNEVALUABLE_SKIP_REASONS,
  VALUELESS_OPERATORS,
} from '../constants';
import {
  ColorGateCondition,
  ColorGateConditionInput,
  ColorOperator,
  ColorOperatorName,
  ColorScoringCase,
  ColorScoringCaseInput,
  ConditionFormValue,
  ConditionRowErrorTarget,
  ConditionRowIssue,
  ConditionValueControl,
  DryRunSkippedRule,
  MatchingCondition,
  MatchingConditionInput,
  MatchingEntity,
  MatchingFieldRefInput,
  MatchingGrade,
  MatchingGradeGroup,
  MatchingGradeGroupInput,
  MatchingGradeName,
  MatchingOperator,
  MatchingOperatorName,
  MatchingRuleSource,
  MatchingRuleSourceInput,
  MatchingSourceKind,
  Rule,
  RuleCondition,
  RuleConditionInput,
  RuleField,
  RuleFamily,
  RuleFieldType,
  RuleLogic,
  RuleOperator,
  RuleOperatorName,
  RuleSetVersion,
  RuleSeverityOverride,
  RuleTestOutcome,
  SeverityOverrideFormValue,
} from '../types';

// A manually-thrown `HttpException(message, 4xx)` puts its string on
// `extensions.originalError.message`, NOT in the `{field, message}[]` shape `extractValidationErrors`
// (`@app/query`) reads off the ValidationPipe's errors.
export const getGraphQLErrorMessage = (e: unknown): string | undefined => {
  if (!(e instanceof ApolloError)) {
    return undefined;
  }

  const graphQLError = e.graphQLErrors[0];

  if (!graphQLError) {
    return e.message;
  }

  const originalError = graphQLError.extensions?.originalError as { message?: unknown } | undefined;

  return typeof originalError?.message === 'string' ? originalError.message : graphQLError.message;
};

// A GENUINE "already imported" outcome, matched by substring: the empty state refetches on it, so a
// stale "no rules yet" panel self-heals instead of leaving the button clickable forever.
export const isAlreadyImportedMessage = (message: string | undefined): boolean =>
  !!message && message.includes('already imported');

// Apollo still executes a `refetch()` call on a `skip`-ped query — with whatever `variables` it was
// last given, which for a query gated on a not-yet-loaded id is `undefined`. Guard any refetch of a
// possibly-skipped query with the SAME condition that gates it; the next render re-runs it anyway
// once the query stops being skipped.
export const refetchUnlessSkipped = <T>(refetch: () => Promise<T>, skip: boolean): Promise<T | undefined> =>
  skip ? Promise.resolve(undefined) : refetch();

export const parseJsonObject = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(raw);

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

// ── Dry-run skip diagnostics ────────────────────────────────────────────────────────────────────
// A rule that produced no finding either did not match or could not be evaluated. Only the second is
// worth reporting: `notApplicable` would otherwise list most of the catalogue on every run.
export const unevaluableSkips = (skipped: DryRunSkippedRule[] | undefined): DryRunSkippedRule[] =>
  (skipped ?? []).filter(skip => UNEVALUABLE_SKIP_REASONS.includes(skip.reason));

export const getRuleTestOutcome = (fired: boolean, skip: DryRunSkippedRule | null | undefined): RuleTestOutcome => {
  if (fired) {
    return 'fired';
  }

  return skip && UNEVALUABLE_SKIP_REASONS.includes(skip.reason) ? 'unevaluable' : 'notFired';
};

// ── Operator spellings ──────────────────────────────────────────────────────────────────────────
// A GraphQL enum travels as its NAME while stored JSON holds its VALUE, so these two functions are
// the only crossings between the spellings.
const OPERATOR_ENTRIES = Object.entries(RuleOperator) as [RuleOperatorName, RuleOperator][];

const OPERATOR_BY_WIRE_NAME = new Map<string, RuleOperator>(OPERATOR_ENTRIES);
const WIRE_NAME_BY_OPERATOR = new Map<RuleOperator, RuleOperatorName>(
  OPERATOR_ENTRIES.map(([name, operator]) => [operator, name]),
);

export const operatorFromWireName = (name: string | undefined): RuleOperator | undefined =>
  name ? OPERATOR_BY_WIRE_NAME.get(name) : undefined;

export const operatorWireName = (operator: RuleOperator): RuleOperatorName =>
  WIRE_NAME_BY_OPERATOR.get(operator) as RuleOperatorName;

// An unknown operator yields `undefined` rather than a cast, so a hand-edited row renders as "no
// operator" instead of crashing the editor.
export const operatorFromStoredValue = (value: string | undefined): RuleOperator | undefined =>
  OPERATOR_ENTRIES.find(([, operator]) => operator === value)?.[1];

// The operator subset a field allows, as shipped BY the server (`RuleField.operators`, VALUE
// spelling — it is a plain `[String]`, not the enum) — never re-derived here.
export const legalOperatorsFor = (field: RuleField | undefined): RuleOperator[] =>
  (field?.operators ?? [])
    .map(value => operatorFromStoredValue(value))
    .filter((operator): operator is RuleOperator => !!operator);

// ── Condition rows ──────────────────────────────────────────────────────────────────────────────
export const getConditionValueControl = (
  fieldType: RuleFieldType | undefined,
  operator: RuleOperator | '' | undefined,
): ConditionValueControl => {
  if (!fieldType || !operator) {
    return 'none';
  }

  if (VALUELESS_OPERATORS.includes(operator)) {
    return 'none';
  }

  if (LIST_OPERATORS.includes(operator)) {
    return 'optionMultiSelect';
  }

  if (NUMERIC_OPERATORS.includes(operator)) {
    return 'number';
  }

  if (fieldType === RuleFieldType.boolean) {
    return 'boolean';
  }

  if (fieldType === RuleFieldType.select) {
    return 'optionSelect';
  }

  return fieldType === RuleFieldType.number ? 'number' : 'text';
};

let uidCounter = 0;

// The key must follow the ROW, not its index: index keys shift rows into a different component
// instance on a remove or reorder.
export const nextRowUid = (prefix: string): string => {
  uidCounter += 1;

  return `${prefix}-${uidCounter}`;
};

export const emptyConditionRow = (): ConditionFormValue => ({
  uid: nextRowUid('condition'),
  field: '',
  operator: '',
  value: '',
  values: [],
});

export const toConditionRow = (condition: RuleCondition): ConditionFormValue => {
  const operator = operatorFromStoredValue(condition.operator) ?? '';
  const { value } = condition;

  return {
    uid: nextRowUid('condition'),
    field: condition.field ?? '',
    operator,
    value: Array.isArray(value) || value === undefined || value === null ? '' : String(value),
    values: Array.isArray(value) ? value.map(item => String(item)) : [],
  };
};

export const toOverrideRow = (override: RuleSeverityOverride): SeverityOverrideFormValue => ({
  uid: nextRowUid('override'),
  logic: override.logic,
  severity: override.severity,
  conditions: (override.conditions ?? []).map(toConditionRow),
});

// The one place a form row becomes a typed condition. `value` is typed here, never in a control:
// the server re-checks the same matrix and a string "3" on a number field is a 400, not a coercion.
export const toConditionInput = (
  row: ConditionFormValue,
  fieldsByPath: Map<string, RuleField>,
): RuleConditionInput | null => {
  const field = fieldsByPath.get(row.field);
  const operator = row.operator || undefined;

  if (!field || !operator) {
    return null;
  }

  const control = getConditionValueControl(field.type, operator);
  const base = { field: row.field, operator: operatorWireName(operator) };

  switch (control) {
    case 'none':
      return base;
    case 'boolean':
      return { ...base, value: row.value === 'true' };
    case 'number':
      return { ...base, value: Number(row.value) };
    case 'optionMultiSelect':
      return { ...base, value: row.values ?? [] };
    default:
      return { ...base, value: row.value };
  }
};

export type Translate = (key: string) => string;

const issue = (target: ConditionRowErrorTarget, message: string): ConditionRowIssue => ({ target, message });

const valueIssue = (message: string): ConditionRowIssue => issue(ConditionRowErrorTarget.value, message);

// The i18n'd reason this row cannot be submitted, or `undefined`. A blank value has no faithful
// reading — `''`, `0` and `[]` are all values the engine MATCHES ON — so it is refused, not sent.
export const conditionRowIssue = (
  row: ConditionFormValue,
  fieldsByPath: Map<string, RuleField>,
  translate: Translate,
): ConditionRowIssue | undefined => {
  const field = fieldsByPath.get(row.field);

  if (!field) {
    return issue(ConditionRowErrorTarget.field, translate('rules.editor.validation.fieldRequired'));
  }

  if (!row.operator) {
    return issue(ConditionRowErrorTarget.operator, translate('rules.editor.validation.operatorRequired'));
  }

  const control = getConditionValueControl(field.type, row.operator);

  if (control === 'none') {
    return undefined;
  }

  if (control === 'optionMultiSelect') {
    return (row.values ?? []).length ? undefined : valueIssue(translate('rules.editor.validation.valuesRequired'));
  }

  const scalar = typeof row.value === 'string' ? row.value : '';

  if (control === 'number') {
    return NUMERIC_VALUE_PATTERN.test(scalar.trim())
      ? undefined
      : valueIssue(translate('rules.editor.validation.numberValue'));
  }

  if (control === 'boolean') {
    return BOOLEAN_VALUES.some(option => option === scalar)
      ? undefined
      : valueIssue(translate('rules.editor.validation.valueRequired'));
  }

  return scalar.trim() ? undefined : valueIssue(translate('rules.editor.validation.valueRequired'));
};

export const conditionRowError = (
  row: ConditionFormValue,
  fieldsByPath: Map<string, RuleField>,
  translate: Translate,
): string | undefined => conditionRowIssue(row, fieldsByPath, translate)?.message;

// `section` is free text with autocomplete over what the family already uses — no new GraphQL op,
// the list is derived from the loaded rules.
export const collectSections = (rules: Rule[]): string[] =>
  [...new Set(rules.map(rule => rule.section).filter((section): section is string => !!section))].sort();

// ── Colour coding (color-coding-rules-ui) ───────────────────────────────────────────────────────
// One row shape for BOTH a scoring rule's `cases[]` (fixed `field`, has `points`) and a gate's
// `conditions[]` (own `field`, no `points`) — `ColorCaseRow` renders whichever half applies.
export interface ColorCaseFormValue {
  uid: string;
  field: string;
  operator: ColorOperator | '';
  value: string;
  values: string[];
  points: string;
}

// ⚠ `ColorOperator`'s NAME differs from its VALUE for 3/5 members — the same crossing
// `operatorFromWireName`/`operatorWireName` do for `RuleOperator` above, applied to the colour
// vocabulary. `field.operators` (from `fieldCatalogueGetMany`) and stored `cases[]`/`conditions[]`
// both carry the VALUE; a mutation's `operator` argument needs the NAME.
const COLOR_OPERATOR_ENTRIES = Object.entries(ColorOperator) as [ColorOperatorName, ColorOperator][];

const COLOR_OPERATOR_BY_WIRE_VALUE = new Map<string, ColorOperator>(
  COLOR_OPERATOR_ENTRIES.map(([, value]) => [value, value]),
);
const COLOR_OPERATOR_WIRE_NAME = new Map<ColorOperator, ColorOperatorName>(
  COLOR_OPERATOR_ENTRIES.map(([name, value]) => [value, name]),
);

export const colorOperatorFromWireValue = (value: string | undefined): ColorOperator | undefined =>
  value ? COLOR_OPERATOR_BY_WIRE_VALUE.get(value) : undefined;

export const colorOperatorWireName = (operator: ColorOperator): ColorOperatorName =>
  COLOR_OPERATOR_WIRE_NAME.get(operator) as ColorOperatorName;

// The operator subset a field allows, shipped BY the server (`RuleField.operators`, VALUE spelling)
// — never re-derived here.
export const legalColorOperatorsFor = (field: RuleField | undefined): ColorOperator[] =>
  (field?.operators ?? [])
    .map(value => colorOperatorFromWireValue(value))
    .filter((operator): operator is ColorOperator => !!operator);

export const getColorValueControl = (
  fieldType: RuleFieldType | undefined,
  operator: ColorOperator | '' | undefined,
): ConditionValueControl => {
  if (!fieldType || !operator) {
    return 'none';
  }

  if (COLOR_VALUELESS_OPERATORS.includes(operator)) {
    return 'none';
  }

  // `in`/`not_in` on a non-select field (the seed's `country not_in [US,CN,RU]`) is a free-typed
  // list — a single text box would hide the stored list and submit `""` over it.
  if (COLOR_LIST_VALUE_OPERATORS.includes(operator)) {
    return fieldType === RuleFieldType.select ? 'optionMultiSelect' : 'textList';
  }

  if (fieldType === RuleFieldType.select) {
    return 'optionSelect';
  }

  if (fieldType === RuleFieldType.boolean) {
    return 'boolean';
  }

  return fieldType === RuleFieldType.number ? 'number' : 'text';
};

export const emptyColorCaseRow = (): ColorCaseFormValue => ({
  uid: nextRowUid('colorCase'),
  field: '',
  operator: '',
  value: '',
  values: [],
  points: '',
});

export const toColorCaseRow = (item: ColorScoringCase): ColorCaseFormValue => ({
  uid: nextRowUid('colorCase'),
  field: '',
  operator: colorOperatorFromWireValue(item.operator) ?? '',
  value: Array.isArray(item.value) || item.value === undefined || item.value === null ? '' : String(item.value),
  values: Array.isArray(item.value) ? item.value.map(entry => String(entry)) : [],
  points: String(item.points ?? ''),
});

export const toColorConditionRow = (condition: ColorGateCondition): ColorCaseFormValue => ({
  uid: nextRowUid('colorCondition'),
  field: condition.field,
  operator: colorOperatorFromWireValue(condition.operator) ?? '',
  value:
    Array.isArray(condition.value) || condition.value === undefined || condition.value === null
      ? ''
      : String(condition.value),
  values: Array.isArray(condition.value) ? condition.value.map(entry => String(entry)) : [],
  points: '',
});

// One field controls the TYPE for every case of a scoring rule — passed in, never read off the row.
const colorRowValue = (
  row: ColorCaseFormValue,
  control: ConditionValueControl,
  fieldType: RuleFieldType | undefined,
): unknown => {
  switch (control) {
    case 'none':
      return undefined;
    case 'boolean':
      return row.value === 'true';
    case 'number':
      return Number(row.value);
    case 'optionMultiSelect':
      return row.values ?? [];
    case 'textList':
      return fieldType === RuleFieldType.number ? (row.values ?? []).map(Number) : row.values ?? [];
    default:
      return row.value;
  }
};

// A scoring case's `points` is a SIGNED integer — never validated as `>= 0`.
export const toColorCaseInput = (
  row: ColorCaseFormValue,
  field: RuleField | undefined,
): ColorScoringCaseInput | null => {
  const operator = row.operator || undefined;
  const points = Number(row.points);

  if (!field || !operator || !(row.points ?? '').trim() || Number.isNaN(points) || !Number.isInteger(points)) {
    return null;
  }

  const control = getColorValueControl(field.type, operator);
  const value = colorRowValue(row, control, field.type);

  return { operator: colorOperatorWireName(operator), points, ...(control === 'none' ? {} : { value }) };
};

export const toColorConditionInput = (
  row: ColorCaseFormValue,
  fieldsByPath: Map<string, RuleField>,
): ColorGateConditionInput | null => {
  const field = fieldsByPath.get(row.field);
  const operator = row.operator || undefined;

  if (!field || !operator) {
    return null;
  }

  const control = getColorValueControl(field.type, operator);
  const value = colorRowValue(row, control, field.type);

  return { field: row.field, operator: colorOperatorWireName(operator), ...(control === 'none' ? {} : { value }) };
};

// The i18n'd reason a colour case/condition row cannot be submitted. `requireField`: on for a gate
// condition (its own field per row), off for a scoring case (the field is fixed at the rule level).
export const colorRowIssue = (
  row: ColorCaseFormValue,
  field: RuleField | undefined,
  translate: Translate,
  options: { requireField: boolean; requirePoints: boolean },
): ConditionRowIssue | undefined => {
  if (options.requireField && !field) {
    return issue(ConditionRowErrorTarget.field, translate('rules.editor.validation.fieldRequired'));
  }

  if (!row.operator) {
    return issue(ConditionRowErrorTarget.operator, translate('rules.editor.validation.operatorRequired'));
  }

  // Formik's `prepareDataForValidation` hands yup `undefined` for every `''`, so this runs on
  // rows whose empty strings are gone — read them as `conditionRowError` does, never `.trim()` raw.
  const points = typeof row.points === 'string' ? row.points : '';
  const scalar = typeof row.value === 'string' ? row.value : '';
  const list = row.values ?? [];

  if (options.requirePoints) {
    const parsed = Number(points);

    if (!points.trim() || Number.isNaN(parsed) || !Number.isInteger(parsed)) {
      return issue(ConditionRowErrorTarget.points, translate('rules.colorCoding.validation.pointsRequired'));
    }
  }

  if (!field) {
    return undefined;
  }

  const control = getColorValueControl(field.type, row.operator);

  if (control === 'none') {
    return undefined;
  }

  if (control === 'optionMultiSelect') {
    return list.length ? undefined : valueIssue(translate('rules.editor.validation.valuesRequired'));
  }

  if (control === 'textList') {
    if (!list.length) {
      return valueIssue(translate('rules.editor.validation.valuesRequired'));
    }

    return field.type === RuleFieldType.number && !list.every(entry => NUMERIC_VALUE_PATTERN.test(entry.trim()))
      ? valueIssue(translate('rules.editor.validation.numberValue'))
      : undefined;
  }

  if (control === 'number') {
    return NUMERIC_VALUE_PATTERN.test(scalar.trim())
      ? undefined
      : valueIssue(translate('rules.editor.validation.numberValue'));
  }

  if (control === 'boolean') {
    return BOOLEAN_VALUES.some(option => option === scalar)
      ? undefined
      : valueIssue(translate('rules.editor.validation.valueRequired'));
  }

  return scalar.trim() ? undefined : valueIssue(translate('rules.editor.validation.valueRequired'));
};

export const colorRowError = (
  row: ColorCaseFormValue,
  field: RuleField | undefined,
  translate: Translate,
  options: { requireField: boolean; requirePoints: boolean },
): string | undefined => colorRowIssue(row, field, translate, options)?.message;

// Mirrors the backend's `FAMILY_CHILDREN[family].count` (`family-children.config.ts`) — used
// wherever the FE previously read `rules.length` (the empty-state gate, the publish/discard button,
// the discard/restore explanation text), which is always `[]` for a colour/matching version.
export const familyRuleCount = (family: RuleFamily, version: RuleSetVersion | null | undefined): number => {
  if (!version) {
    return 0;
  }

  if (family === RuleFamily.colorCoding) {
    return (version.colorScoringRules?.length ?? 0) + (version.colorGates?.length ?? 0);
  }

  if (family === RuleFamily.matching) {
    return version.matchingRules?.length ?? 0;
  }

  return version.rules?.length ?? 0;
};

// Mirrors the backend's publish check (`rules.service.ts` `publishColorCoding`): a colour draft needs a
// scoring rule AND a band — gates alone are refused. Kept apart from `familyRuleCount`, which feeds the dialog counts.
export const canPublishFamily = (family: RuleFamily, version: RuleSetVersion | null | undefined): boolean => {
  if (family === RuleFamily.colorCoding) {
    return !!version?.colorScoringRules?.length && !!version?.colorBands?.length;
  }

  return familyRuleCount(family, version) > 0;
};

// ── Matching (03-matching) ──────────────────────────────────────────────────────────────────────
// ⚠ `MatchingOperator`'s NAME differs from its VALUE for most members — same crossing
// `operatorFromWireName`/`operatorWireName` do for `RuleOperator` above, applied to the matching
// vocabulary. `field.operators` (from `fieldCatalogueGetMany`) and stored `grades[].conditions[]`
// both carry the VALUE; a mutation's `operator` argument needs the NAME.
const MATCHING_OPERATOR_ENTRIES = Object.entries(MatchingOperator) as [MatchingOperatorName, MatchingOperator][];

const MATCHING_OPERATOR_BY_WIRE_VALUE = new Map<string, MatchingOperator>(
  MATCHING_OPERATOR_ENTRIES.map(([, value]) => [value, value]),
);
const MATCHING_OPERATOR_WIRE_NAME = new Map<MatchingOperator, MatchingOperatorName>(
  MATCHING_OPERATOR_ENTRIES.map(([name, value]) => [value, name]),
);

export const matchingOperatorFromWireValue = (value: string | undefined): MatchingOperator | undefined =>
  value ? MATCHING_OPERATOR_BY_WIRE_VALUE.get(value) : undefined;

export const matchingOperatorWireName = (operator: MatchingOperator): MatchingOperatorName =>
  MATCHING_OPERATOR_WIRE_NAME.get(operator) as MatchingOperatorName;

// The operator subset a field allows, shipped BY the server (`RuleField.operators`, VALUE spelling)
// — never re-derived here.
export const legalMatchingOperatorsFor = (field: RuleField | undefined): MatchingOperator[] =>
  (field?.operators ?? [])
    .map(value => matchingOperatorFromWireValue(value))
    .filter((operator): operator is MatchingOperator => !!operator);

export const matchingOperatorNeedsRight = (operator: MatchingOperator | '' | undefined): boolean =>
  !!operator && MATCHING_CROSS_ENTITY_OPERATORS.includes(operator);

// The value control for a matching condition's LEFT-typed value. The two date-diff operators are
// cross-entity AND carry a numeric window value (days/months) regardless of the field's own type.
export const getMatchingValueControl = (
  fieldType: RuleFieldType | undefined,
  operator: MatchingOperator | '' | undefined,
): ConditionValueControl => {
  if (!fieldType || !operator) {
    return 'none';
  }

  if (MATCHING_VALUELESS_OPERATORS.includes(operator) || MATCHING_RIGHT_ONLY_OPERATORS.includes(operator)) {
    return 'none';
  }

  if (MATCHING_NUMERIC_OPERATORS.includes(operator)) {
    return 'number';
  }

  if (MATCHING_LIST_VALUE_OPERATORS.includes(operator)) {
    return fieldType === RuleFieldType.select || fieldType === RuleFieldType.multiSelect
      ? 'optionMultiSelect'
      : 'textList';
  }

  if (fieldType === RuleFieldType.select) {
    return 'optionSelect';
  }

  if (fieldType === RuleFieldType.boolean) {
    return 'boolean';
  }

  return fieldType === RuleFieldType.number ? 'number' : 'text';
};

// One row shape for a matching rule's `sources[]` — EITHER a catalogue field (own entity + path) OR
// a Notes-section name, never both.
export interface MatchingSourceFormValue {
  uid: string;
  entity: MatchingEntity | '';
  sourceKind: MatchingSourceKind;
  field: string;
  notesSection: string;
}

export const emptyMatchingSourceRow = (): MatchingSourceFormValue => ({
  uid: nextRowUid('matchingSource'),
  entity: '',
  sourceKind: MatchingSourceKind.field,
  field: '',
  notesSection: '',
});

export const toMatchingSourceRow = (source: MatchingRuleSource): MatchingSourceFormValue => ({
  uid: nextRowUid('matchingSource'),
  entity: source.entity,
  sourceKind: source.notesSection ? MatchingSourceKind.notes : MatchingSourceKind.field,
  field: source.field ?? '',
  notesSection: source.notesSection ?? '',
});

export const toMatchingSourceInput = (row: MatchingSourceFormValue): MatchingRuleSourceInput | null => {
  if (!row.entity) {
    return null;
  }

  if (row.sourceKind === MatchingSourceKind.notes) {
    return row.notesSection.trim() ? { entity: row.entity, notesSection: row.notesSection.trim() } : null;
  }

  return row.field ? { entity: row.entity, field: row.field } : null;
};

export const matchingSourceRowError = (row: MatchingSourceFormValue, translate: Translate): string | undefined => {
  if (!row.entity) {
    return translate('rules.matching.validation.sourceEntityRequired');
  }

  if (row.sourceKind === MatchingSourceKind.notes) {
    if (!row.notesSection.trim()) {
      return translate('rules.matching.validation.sourceNotesRequired');
    }

    return MATCHING_NOTES_SECTION_VALUES.includes(row.notesSection)
      ? undefined
      : translate('rules.matching.validation.sourceNotesUnknown');
  }

  return row.field ? undefined : translate('rules.matching.validation.sourceFieldRequired');
};

// One row shape for an `exact` matching rule's condition — `left`/`right` each pick their OWN
// entity, because a matching condition may compare a GC field against an IP field.
export interface MatchingConditionFormValue {
  uid: string;
  leftEntity: MatchingEntity | '';
  leftField: string;
  operator: MatchingOperator | '';
  rightEntity: MatchingEntity | '';
  rightField: string;
  value: string;
  values: string[];
}

export const emptyMatchingConditionRow = (): MatchingConditionFormValue => ({
  uid: nextRowUid('matchingCondition'),
  leftEntity: '',
  leftField: '',
  operator: '',
  rightEntity: '',
  rightField: '',
  value: '',
  values: [],
});

export const toMatchingConditionRow = (condition: MatchingCondition): MatchingConditionFormValue => {
  const operator = matchingOperatorFromWireValue(condition.operator) ?? '';
  const { value } = condition;

  return {
    uid: nextRowUid('matchingCondition'),
    leftEntity: condition.left?.entity ?? '',
    leftField: condition.left?.field ?? '',
    operator,
    rightEntity: condition.right?.entity ?? '',
    rightField: condition.right?.field ?? '',
    value: Array.isArray(value) || value === undefined || value === null ? '' : String(value),
    values: Array.isArray(value) ? value.map(item => String(item)) : [],
  };
};

const matchingConditionRowValue = (
  row: MatchingConditionFormValue,
  control: ConditionValueControl,
  fieldType: RuleFieldType | undefined,
): unknown => {
  switch (control) {
    case 'none':
      return undefined;
    case 'boolean':
      return row.value === 'true';
    case 'number':
      return Number(row.value);
    case 'optionMultiSelect':
      return row.values ?? [];
    case 'textList':
      return fieldType === RuleFieldType.number ? (row.values ?? []).map(Number) : row.values ?? [];
    default:
      return row.value;
  }
};

export const toMatchingConditionInput = (
  row: MatchingConditionFormValue,
  fieldsByEntityAndPath: Map<string, RuleField>,
): MatchingConditionInput | null => {
  const operator = row.operator || undefined;

  if (!row.leftEntity || !row.leftField || !operator) {
    return null;
  }

  const left: MatchingFieldRefInput = { entity: row.leftEntity, field: row.leftField };
  const leftCatalogueField = fieldsByEntityAndPath.get(`${row.leftEntity}:${row.leftField}`);
  const needsRight = matchingOperatorNeedsRight(operator);

  if (needsRight && (!row.rightEntity || !row.rightField)) {
    return null;
  }

  const right: MatchingFieldRefInput | undefined = needsRight
    ? { entity: row.rightEntity as MatchingEntity, field: row.rightField }
    : undefined;

  const control = getMatchingValueControl(leftCatalogueField?.type, operator);
  const value = control === 'none' ? undefined : matchingConditionRowValue(row, control, leftCatalogueField?.type);

  return {
    left,
    operator: matchingOperatorWireName(operator),
    ...(right ? { right } : {}),
    ...(control === 'none' ? {} : { value }),
  };
};

// ⚠ `MatchingGrade`'s NAME differs from its VALUE for one member (`notExcluded`/`not_excluded`) —
// same crossing pattern as the operator/field-type enums above.
const MATCHING_GRADE_ENTRIES = Object.entries(MatchingGrade) as [MatchingGradeName, MatchingGrade][];

const MATCHING_GRADE_BY_WIRE_VALUE = new Map<string, MatchingGrade>(MATCHING_GRADE_ENTRIES.map(([, v]) => [v, v]));
const MATCHING_GRADE_WIRE_NAME = new Map<MatchingGrade, MatchingGradeName>(
  MATCHING_GRADE_ENTRIES.map(([name, value]) => [value, name]),
);

export const matchingGradeFromWireValue = (value: string | undefined): MatchingGrade | undefined =>
  value ? MATCHING_GRADE_BY_WIRE_VALUE.get(value) : undefined;

export const matchingGradeWireName = (grade: MatchingGrade): MatchingGradeName =>
  MATCHING_GRADE_WIRE_NAME.get(grade) as MatchingGradeName;

// i18n keys (`rules.matching.grade.option.*`) are the enum NAMES; results carry the wire VALUE.
export const matchingGradeLabelKey = (value: string): string =>
  `rules.matching.grade.option.${MATCHING_GRADE_WIRE_NAME.get(value as MatchingGrade) ?? value}`;

// One `exact` matching rule's ordered grade/condition group — first whose conditions hold wins.
export interface MatchingGradeGroupFormValue {
  uid: string;
  grade: MatchingGrade | '';
  logic: RuleLogic;
  conditions: MatchingConditionFormValue[];
}

export const emptyMatchingGradeGroupRow = (logic: RuleLogic): MatchingGradeGroupFormValue => ({
  uid: nextRowUid('matchingGrade'),
  grade: '',
  logic,
  conditions: [emptyMatchingConditionRow()],
});

export const toMatchingGradeGroupRow = (group: MatchingGradeGroup): MatchingGradeGroupFormValue => ({
  uid: nextRowUid('matchingGrade'),
  grade: matchingGradeFromWireValue(group.grade) ?? '',
  logic: group.logic,
  conditions: group.conditions.length ? group.conditions.map(toMatchingConditionRow) : [emptyMatchingConditionRow()],
});

export const toMatchingGradeGroupInput = (
  row: MatchingGradeGroupFormValue,
  fieldsByEntityAndPath: Map<string, RuleField>,
): MatchingGradeGroupInput | null => {
  if (!row.grade) {
    return null;
  }

  const conditions = row.conditions
    .map(condition => toMatchingConditionInput(condition, fieldsByEntityAndPath))
    .filter((condition): condition is MatchingConditionInput => !!condition);

  if (!conditions.length) {
    return null;
  }

  return { grade: matchingGradeWireName(row.grade), logic: row.logic, conditions };
};

export const matchingConditionRowIssue = (
  row: MatchingConditionFormValue,
  fieldsByEntityAndPath: Map<string, RuleField>,
  translate: Translate,
): ConditionRowIssue | undefined => {
  if (!row.leftEntity || !row.leftField) {
    return issue(ConditionRowErrorTarget.field, translate('rules.editor.validation.fieldRequired'));
  }

  if (!row.operator) {
    return issue(ConditionRowErrorTarget.operator, translate('rules.editor.validation.operatorRequired'));
  }

  const needsRight = matchingOperatorNeedsRight(row.operator);

  if (needsRight && (!row.rightEntity || !row.rightField)) {
    return issue(ConditionRowErrorTarget.rightField, translate('rules.matching.validation.rightFieldRequired'));
  }

  const leftCatalogueField = fieldsByEntityAndPath.get(`${row.leftEntity}:${row.leftField}`);
  const control = getMatchingValueControl(leftCatalogueField?.type, row.operator);

  if (control === 'none') {
    return undefined;
  }

  const scalar = typeof row.value === 'string' ? row.value : '';
  const list = row.values ?? [];

  if (control === 'optionMultiSelect' || control === 'textList') {
    return list.length ? undefined : valueIssue(translate('rules.editor.validation.valuesRequired'));
  }

  if (control === 'number') {
    return NUMERIC_VALUE_PATTERN.test(scalar.trim())
      ? undefined
      : valueIssue(translate('rules.editor.validation.numberValue'));
  }

  if (control === 'boolean') {
    return BOOLEAN_VALUES.some(option => option === scalar)
      ? undefined
      : valueIssue(translate('rules.editor.validation.valueRequired'));
  }

  return scalar.trim() ? undefined : valueIssue(translate('rules.editor.validation.valueRequired'));
};

export const matchingConditionRowError = (
  row: MatchingConditionFormValue,
  fieldsByEntityAndPath: Map<string, RuleField>,
  translate: Translate,
): string | undefined => matchingConditionRowIssue(row, fieldsByEntityAndPath, translate)?.message;
